import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  Suspense,
} from "react";
import axios from "axios";
import _ from "lodash";
import Cookie from "universal-cookie";
import { DataContext } from "./DataContext";
import {
  faCube,
  faChartBar,
  faChartLine,
  faWater,
  faGaugeHigh,
  faSliders,
  faTable,
  faFont,
  faLightbulb,
  faToggleOn,
  faList,
  faEdit,
  faUserShield,
  faMapMarkedAlt,
  faImage,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// Lazy load widget components to reduce initial bundle size
const GaugeWidget = React.lazy(() => import("./GaugeWidget"));
const LineChart = React.lazy(() => import("./Chart.js"));
const FluidTank = React.lazy(() => import("./FluidTank"));
const BarChart = React.lazy(() => import("./BarChart"));
const CustomGauge = React.lazy(() => import("./CustomGauge"));
const EquilizerBar = React.lazy(() => import("./EquilizerBar"));
const MqttWidget = React.lazy(() => import("./MqttWidget"));
const MqttIndicator = React.lazy(() => import("./MqttIndicator"));
const RealtimeTable = React.lazy(() => import("./RealtimeTable"));
const TextWidget = React.lazy(() => import("./TextWidget"));
const DynamicTextWidget = React.lazy(() => import("./DynamicTextWidget"));
const CustomFormWidget = React.lazy(() => import("./CustomFormWidget.js"));
const EditableTable = React.lazy(() => import("./EditableTable"));
const ImageWidget = React.lazy(() => import("./ImageWidget.js"));
const MapWidget = React.lazy(() => import("./MapWidget"));
const PushButton = React.lazy(() => import("./PushButton"));
const SwitchButton = React.lazy(() => import("./SwitchButton"));
const AccessListWidget = React.lazy(() => import("./AccessListWidget"));

const widgetIcons = {
  gauge: faGaugeHigh,
  bar_chart: faChartBar,
  realtime_chart: faChartLine,
  fluid_tank: faWater,
  custom_gauge: faSliders,
  equilizer_bar: faChartBar,
  mqtt_writer: faEdit,
  mqtt_indicator: faLightbulb,
  realtime_table: faTable,
  text_widget: faFont,
  dynamic_text_widget: faFont,
  editable_table_widget: faTable,
  access_list_widget: faUserShield,
  push_button: faToggleOn,
  switch_button: faToggleOn,
  map_widget: faMapMarkedAlt,
  nested_pane: faList,
  image_widget: faImage,
  none: faCube, // fallback for "none"
};

function Widget({
  options,
  widgetPath,
  paneArray,
  setPaneArray,
  ...otherProps
}) {
  const cookies = new Cookie();
  const userToken = cookies.get("userToken");
  const headers = {
    Authorization: `Bearer ${userToken}`,
    "Content-Type": "application/json",
  };
  // Updated context destructuring: removed registerSource and unregisterSource
  const { data, subscribe } = React.useContext(DataContext);
  const widgetRef = useRef(null);
  const [isVisible, setIsVisible] = useState(true);
  const [widgetVal, setWidgetVal] = useState(otherProps.value || "N/A");
  const [chartValues, setChartValues] = useState([]);
  const [mapVehicles, setMapVehicles] = useState([]);
  const [equilizerData, setEquilizerData] = useState({
    normalized: [],
    raw: [],
  });
  const [barChartData, setBarChartData] = useState(() => {
    const numberOfBars = otherProps.widget?.numberOfBars || 5;
    return {
      labels: Array(numberOfBars)
        .fill("")
        .map((_, i) => `Bar ${i + 1}`),
      datasets: [
        {
          label:
            otherProps.widget?.datasource?.[0]?.name ||
            "Please Add Source to view data",
          data: Array(numberOfBars).fill(0),
          backgroundColor:
            otherProps.widget?.datasets?.[0]?.bar_bg_color ||
            "rgba(75,192,192,0.6)",
          borderColor:
            otherProps.widget?.datasets?.[0]?.bar_br_color ||
            "rgba(75,192,192,1)",
          borderWidth: 1,
        },
      ],
    };
  });
  const [alarms, setAlarms] = useState(otherProps.widget?.alarms || []);
  // Normalize alarm direction for consistency
  const normalizeDirection = useCallback((direction) => {
    switch (direction) {
      case ">":
        return "greater";
      case "<":
        return "less";
      case "=":
        return "equal";
      case ">=":
        return "greater_equal";
      case "<=":
        return "less_equal";
      default:
        return direction;
    }
  }, []);

  // Fetch alarms for the widget's data sources
  const fetchAlarms = useCallback(
    async (sourceIds) => {
      if (!sourceIds || !sourceIds.length) {
        setAlarms([]);
        return;
      }
      try {
        const responses = await Promise.all(
          sourceIds.map((id) =>
            axios.get(`/logger_sources/alarms/for-source/${id}`, { headers })
          )
        );
        const latestAlarms = responses
          .flatMap((res) => res.data || [])
          .map((alarm) => ({
            ...alarm,
            direction: normalizeDirection(alarm.direction),
          }));
        setAlarms((prev) => {
          const isLatest =
            latestAlarms.length === prev.length &&
            latestAlarms.every((latest, index) => {
              const stored = prev[index];
              return (
                stored &&
                latest.id === stored.id &&
                latest.updatedAt === stored.updatedAt
              );
            });
          if (!isLatest) {
            let dummyPaneArray = [...paneArray];
            let widgetToUpdate = getWidgetByPath();
            widgetToUpdate.alarms = latestAlarms;
            setPaneArray(dummyPaneArray);
            return latestAlarms;
          }
          return prev;
        });
      } catch (error) {
        console.error("Error fetching alarms:", error);
        setAlarms([]); // Reset alarms on error, could enhance with user feedback
      }
    },
    [paneArray, setPaneArray, normalizeDirection]
  );

  // Retrieve widget by its path in the paneArray
  const getWidgetByPath = useCallback(() => {
    if (!paneArray || !widgetPath || !Array.isArray(widgetPath)) return null;
    let current = paneArray;
    for (let i = 0; i < widgetPath.length - 1; i++) {
      if (!current[widgetPath[i]] || !current[widgetPath[i]].widgetsArray)
        return null;
      current = current[widgetPath[i]].widgetsArray;
    }
    return current[widgetPath[widgetPath.length - 1]] || null;
  }, [paneArray, widgetPath]);

  // Memoize source IDs to avoid recalculating unnecessarily
  const sourceIds = useMemo(() => {
    const widget = getWidgetByPath();
    return widget?.datasource?.map((ds) => ds.id).filter(Boolean) || [];
  }, [getWidgetByPath]);

  // Fetch alarms if the widget is configured to display them
  useEffect(() => {
    const widget = getWidgetByPath();
    if (widget?.displayAlarms && sourceIds.length > 0) {
      fetchAlarms(sourceIds);
    } else {
      setAlarms([]);
    }
  }, [fetchAlarms, sourceIds, otherProps.widget?.displayAlarms]);

  // Use IntersectionObserver to manage visibility and subscriptions
  // useEffect(() => {
  //   const observer = new IntersectionObserver(
  //     ([entry]) => {
  //       setIsVisible(entry.isIntersecting);
  //     },
  //     { threshold: 0 }
  //   );
  //   if (widgetRef.current) {
  //     observer.observe(widgetRef.current);
  //   }
  //   return () => {
  //     if (widgetRef.current) {
  //       observer.unobserve(widgetRef.current);
  //     }
  //   };
  // }, [widgetRef]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      { threshold: 0 }
    );

    if (widgetRef.current) {
      observer.observe(widgetRef.current);
    }

    return () => {
      if (widgetRef.current) {
        observer.unobserve(widgetRef.current);
      }
      observer.disconnect(); // ✅ fully clean up observer
    };
  }, [widgetRef]);

  // Initialize chart values for real-time charts
  useEffect(() => {
    const widget = getWidgetByPath();
    if (
      widget?.widget_type === "realtime_chart" &&
      widget.datasource?.length > 0
    ) {
      setChartValues(Array(widget.datasource.length).fill(0));
    }
  }, [getWidgetByPath]);

  // Handle data subscriptions based on visibility
  useEffect(() => {
    if (!isVisible) return;

    const widget = getWidgetByPath();
    if (!widget || !widget.datasource || widget.datasource.length === 0) {
      setWidgetVal(
        widget?.widget_type === "dynamic_text_widget"
          ? "N/A"
          : widget?.widget_type === "mqtt_indicator"
          ? "0"
          : 0
      );
      if (widget?.widget_type === "realtime_chart") {
        setChartValues([]);
      }
      if (widget?.widget_type === "equilizer_bar") {
        setEquilizerData({
          normalized: Array(widget.datasource?.length || 0).fill(0),
          raw: Array(widget.datasource?.length || 0).fill(0),
        });
      }
      if (widget?.widget_type === "bar_chart") {
        const numberOfBars = widget.numberOfBars || 5;
        setBarChartData({
          labels: Array(numberOfBars).fill("No Data"),
          datasets: [
            {
              label: "No Source",
              data: Array(numberOfBars).fill(0),
              backgroundColor: "rgba(75,192,192,0.6)",
              borderColor: "rgba(75,192,192,1)",
              borderWidth: 1,
            },
          ],
        });
      }
      return;
    }
    function normalizeValue(value) {
      if (value === null || value === undefined) {
        return "N/A";
      }

      if (typeof value === "object") {
        try {
          return JSON.stringify(value); // convert object/array to string
        } catch {
          return "N/A"; // fallback if object can’t be stringified
        }
      }
      if (typeof value === "string") {
        try {
          return JSON.parse(value); // string -> object/array
        } catch {
          return "N/A"; // invalid JSON string
        }
      }

      if (typeof value === "number") {
        return isNaN(value) ? 0 : value;
      }

      if (typeof value === "boolean") {
        return value ? "1" : "0";
      }

      // fallback for string, float, etc.
      return value;
    }

    // put this helper near your other helpers
    const parseLatLng = (raw) => {
      // raw can be number, string, object, JSON string, etc.
      try {
        let v = raw;

        if (typeof v === "string") {
          // JSON string or "lat,lng"
          try {
            v = JSON.parse(v);
          } catch {
            const parts = v.split(",").map((x) => parseFloat(x.trim()));
            if (parts.length === 2 && parts.every((n) => Number.isFinite(n))) {
              return { lat: parts[0], lng: parts[1] };
            }
          }
        }

        // Array [lat, lng]
        if (Array.isArray(v) && v.length === 2) {
          const [lat, lng] = v.map(Number);
          if (Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng };
        }

        // Common object shapes
        if (v && typeof v === "object") {
          if (Number.isFinite(v.lat) && Number.isFinite(v.lng))
            return { lat: +v.lat, lng: +v.lng };
          if (Number.isFinite(v.latitude) && Number.isFinite(v.longitude))
            return { lat: +v.latitude, lng: +v.longitude };
          if (v.gps && Number.isFinite(v.gps.lat) && Number.isFinite(v.gps.lon))
            return { lat: +v.gps.lat, lng: +v.gps.lon };
          if (
            v.position &&
            Array.isArray(v.position) &&
            v.position.length === 2
          ) {
            const [lat, lng] = v.position.map(Number);
            if (Number.isFinite(lat) && Number.isFinite(lng))
              return { lat, lng };
          }
        }
      } catch {}

      return null;
    };

    // usage

    const unsubscribes = [];

    if (widget.widget_type === "realtime_chart") {
      widget.datasource.forEach((ds, index) => {
        const sourceName = ds.name;
        const unsubscribe = subscribe(sourceName, (newValue) => {
          setChartValues((prev) => {
            const newValues = [...prev];
            newValues[index] = parseFloat(newValue) || 0;
            return newValues;
          });
        });
        unsubscribes.push(unsubscribe);
      });
    } else if (
      [
        "gauge",
        "custom_gauge",
        "fluid_tank",
        "dynamic_text_widget",
        "mqtt_indicator",
        "editable_table_widget",
        "access_list_widget",
        "switch_button",
        "text_widget",
      ].includes(widget.widget_type)
    ) {
      const sourceName = widget.datasource[0]?.name;

      if (sourceName) {
        const unsubscribe = subscribe(sourceName, (newValue) => {
          setWidgetVal(normalizeValue(newValue));
        });
        unsubscribes.push(unsubscribe);
      }
    } else if (widget.widget_type === "equilizer_bar") {
      const maxExpectedValue = 2000;
      widget.datasource.forEach((ds, index) => {
        const sourceName = ds.name;
        const unsubscribe = subscribe(sourceName, (newValue) => {
          const numericValue = parseFloat(newValue) || 0;
          setEquilizerData((prev) => {
            const newRaw = [...prev.raw];
            newRaw[index] = numericValue;
            let normalizedValue = (numericValue / maxExpectedValue) * 100;
            if (normalizedValue > 0 && normalizedValue < 1) {
              normalizedValue = Math.max(normalizedValue * 10, 1);
            }
            const newNormalized = [...prev.normalized];
            newNormalized[index] = Math.min(Math.max(normalizedValue, 0), 100);
            return { normalized: newNormalized, raw: newRaw };
          });
        });
        unsubscribes.push(unsubscribe);
      });
    } else if (widget.widget_type === "bar_chart") {
      const sourceName = widget.datasource[0]?.name;
      if (sourceName) {
        const numberOfBars = widget.numberOfBars || 5;
        const unsubscribe = subscribe(sourceName, (newValue) => {
          const value = parseFloat(newValue) || 0;
          setBarChartData((prev) => {
            const newData = [...prev.datasets[0].data, value];
            const newLabels = [
              ...prev.labels,
              new Date().toLocaleTimeString("en-IN", {
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
                timeZone: "Asia/Kolkata",
              }),
            ];
            if (newData.length > numberOfBars) {
              newData.shift();
              newLabels.shift();
            }
            return {
              labels: newLabels,
              datasets: [
                {
                  label: sourceName || "Data",
                  data: newData,
                  backgroundColor:
                    widget.datasets?.[0]?.bar_bg_color ||
                    "rgba(75,192,192,0.6)",
                  borderColor:
                    widget.datasets?.[0]?.bar_br_color || "rgba(75,192,192,1)",
                  borderWidth: 1,
                },
              ],
            };
          });
        });
        unsubscribes.push(unsubscribe);
      }
    } else if (widget.widget_type === "map_widget") {
      const deviceCount =
        widget?.datasets?.[0]?.sourceCnt ||
        widget?.datasource?.length ||
        widget?.datasets?.length ||
        1;

      // subscribe per datasource entry (like other widgets)
      widget.datasource.slice(0, deviceCount).forEach((ds, index) => {
        const sourceName = ds?.name;
        if (!sourceName) return;

        // try to get stable identity & label from datasets
        const dset = widget.datasets?.[index] || {};
        const deviceId =
          dset.sourceId || dset.deviceId || dset.deviceIndex || index + 1;
        const deviceName =
          dset.deviceName || dset.name || `Device ${index + 1}`;

        const unsubscribe = subscribe(sourceName, (incoming) => {
          const pos = parseLatLng(incoming);
          if (!pos) return;

          setMapVehicles((prev) => {
            // upsert by deviceId
            const next = [...prev];
            const i = next.findIndex((v) => String(v.id) === String(deviceId));
            const record = {
              id: deviceId,
              deviceId,
              name: deviceName,
              latitude: pos.lat,
              longitude: pos.lng,
              position: [pos.lat, pos.lng],
              updatedAt: Date.now(),
            };
            if (i >= 0) next[i] = { ...next[i], ...record };
            else next.push(record);
            return next;
          });
        });

        unsubscribes.push(unsubscribe);
      });

      // expose to MapWidget like you do for other widget values
      // setMapVehicles(mapVehicles);
    }

    return () => {
      unsubscribes.forEach((unsub) => unsub());
    };
  }, [getWidgetByPath, subscribe, isVisible]);

  // Memoize derived pane ID
  const derivedPaneID = useMemo(() => {
    return widgetPath.length > 0
      ? `pane-${widgetPath[0]}`
      : `pane-${Date.now()}`;
  }, [widgetPath]);

  if (!otherProps.widget) {
    return <div>Widget data not available</div>;
  }

  return (
    <div
      ref={widgetRef}
      style={{
        width: "100%",
        minHeight: "100%",
        position: "relative",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {
        <h3
          style={{
            fontSize: `${otherProps.widget.widgetNameFontSize}px`,
            textAlign: "center",
            flexShrink: 0,
            marginBottom: "10px",
            color: "rgb(202, 202, 202)",
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "6px",
          }}
        >
          <FontAwesomeIcon
            icon={widgetIcons[otherProps.widget.widget_type] || faCube}
            style={{ color: "#a0d2ff" }}
          />
          {otherProps.widget.widget_name}
        </h3>
      }

      <div
        style={{
          flex: 1,
          width: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
        }}
      >
        <Suspense fallback={<div>Loading...</div>}>
          {otherProps.widget.widget_type === "gauge" && (
            <div
              style={{ width: "100%", height: "100%", position: "relative" }}
            >
              <GaugeWidget
                value={widgetVal}
                minValue={otherProps.minValue || 0}
                maxValue={otherProps.maxValue || 100}
                animationSpeed={otherProps.animationSpeed || 32}
                options={options}
                className={otherProps.className || "gauge-canvas"}
                style={{ width: "100%", height: "80%" }}
              />
              <div style={{ textAlign: "center", marginTop: "5px" }}>
                {/* {`${typeof widgetVal === "number" ? widgetVal.toFixed(2) : Number(widgetVal.toFixed(2))} ${otherProps.widget.widget_unit || ""}`}
                 */}
                {`${
                  !isNaN(widgetVal) ? Number(widgetVal).toFixed(2) : widgetVal
                } ${otherProps.widget.widget_unit || ""}`}
              </div>
            </div>
          )}
          {otherProps.widget.widget_type === "bar_chart" && (
            <BarChart
              chartData={barChartData}
              numberOfBars={otherProps.widget.numberOfBars || 5}
            />
          )}
          {otherProps.widget.widget_type === "realtime_chart" && (
            <div
              style={{ width: "100%", height: "100%", position: "relative" }}
            >
              <LineChart
                chartValues={chartValues}
                datasets={otherProps.widget.datasets || []}
                onMouseDown={otherProps.onMouseDown}
                onTouchStart={otherProps.onTouchStart}
              />
            </div>
          )}
          {otherProps.widget.widget_type === "fluid_tank" && (
            <div
              style={{ width: "100%", height: "100%", position: "relative" }}
            >
              <FluidTank
                value={widgetVal}
                lower_limit_value={parseInt(otherProps.widget.lower_limit) || 0}
                upper_limit_value={
                  parseInt(otherProps.widget.upper_limit) || 100
                }
                source={
                  otherProps.widget.datasource?.[0]?.name || "default-source"
                }
              />
            </div>
          )}
          {otherProps.widget.widget_type === "custom_gauge" && (
            <div
              style={{ width: "100%", height: "100%", position: "relative" }}
            >
              <CustomGauge
                source={otherProps.widget.datasource?.[0]?.name || ""}
                gaugeSetting={otherProps.widget.datasets?.[0] || {}}
                value={widgetVal}
                unit={
                  otherProps.widget.datasets?.[0]?.unit ||
                  otherProps.widget.widget_unit ||
                  ""
                }
                style={{ width: "100%", height: "100%" }}
              />
            </div>
          )}
          {otherProps.widget.widget_type === "equilizer_bar" && (
            <EquilizerBar
              sources={otherProps.widget.datasource || []}
              datasets={otherProps.widget.datasets}
              normalizedData={equilizerData.normalized}
              rawData={equilizerData.raw}
            />
          )}
          {otherProps.widget.widget_type === "mqtt_writer" && (
            <MqttWidget
              source={otherProps.widget.datasets?.[0] || {}}
              dashboard_name={otherProps.widget.dashboard_name || ""}
            />
          )}
          {otherProps.widget.widget_type === "mqtt_indicator" && (
            <MqttIndicator
              indicatorData={otherProps.widget.datasets?.[0] || {}}
              value={widgetVal}
            />
          )}
          {otherProps.widget.widget_type === "realtime_table" && (
            <RealtimeTable
              sources={otherProps.widget.datasource || []}
              datasets={otherProps.widget.datasets || []}
              paneID={derivedPaneID}
              widgetProperty={
                otherProps.widget.widgetProperty || otherProps.widget
              }
              style={{ width: "100%", height: "100%" }}
            />
          )}
          {otherProps.widget.widget_type === "text_widget" && (
            <div style={{ width: "100%", height: "100%", display: "flex" }}>
              <TextWidget
                source={otherProps.widget.datasource || ""}
                datasets={otherProps.widget.datasets?.[0] || {}}
                value={widgetVal}
              />
            </div>
          )}
          {otherProps.widget.widget_type === "dynamic_text_widget" && (
            <DynamicTextWidget
              value={widgetVal}
              source={
                otherProps.widget.datasource?.[0]?.name || "default-source"
              }
              datasets={otherProps.widget.datasets?.[0] || {}}
            />
          )}
          {/* {otherProps.widget.widget_type === "custom_form_widget" && (
            <CustomFormWidget datasets={otherProps.widget.datasets?.[0] || {}} />
          )} */}
          {otherProps.widget.widget_type === "editable_table_widget" && (
            <EditableTable
              datasets={otherProps.widget.datasets?.[0] || {}}
              widget_name={otherProps.widget.widget_name}
              id={otherProps.widget.id}
              value={widgetVal}
            />
          )}
          {otherProps.widget.widget_type === "access_list_widget" && (
            <AccessListWidget
              datasets={otherProps.widget.datasets?.[0] || {}}
              widget_name={otherProps.widget.widget_name}
              users={widgetVal}
            />
          )}
          {otherProps.widget.widget_type === "push_button" && (
            <PushButton
              datasets={otherProps.widget.datasets?.[0] || {}}
              widget_name={otherProps.widget.widget_name}
              id={otherProps.widget.id}
            />
          )}
          {otherProps.widget.widget_type === "switch_button" && (
            <SwitchButton
              datasets={otherProps.widget.datasets?.[0] || {}}
              widget_name={otherProps.widget.widget_name}
              id={otherProps.widget.id}
              value={widgetVal}
            />
          )}
          {otherProps.widget.widget_type === "map_widget" && (
            <MapWidget
              id={otherProps.widget.id} // widget ID
              sources={otherProps.widget.datasource || []}
              widget_name={otherProps.widget.widget_name} // name for dashboard
              datasets={otherProps.widget.datasets || []} // MQTT / data
              vehicles_prop={ mapVehicles } // vehicle markers or dynamic points
              // vehicles_prop={[
              //   {
              //     id: 1,
              //     deviceId: 1,
              //     name: "Truck A",
              //     latitude: 18.52043,
              //     longitude: 73.85674,
              //     position: [18.52043, 73.85674],
              //     updatedAt: 1736610000000,
              //   },
              //   {
              //     id: 2,
              //     deviceId: 2,
              //     name: "Truck B",
              //     latitude: 19.07609,
              //     longitude: 72.87769,
              //     position: [19.07609, 72.87769],
              //     updatedAt: 1736610010000,
              //   },
              // ]}
            />
          )}

          {otherProps.widget.widget_type === "image_widget" && (
            <ImageWidget
              widget={otherProps.widget}
              paneArray={paneArray}
              setPaneArray={setPaneArray}
              widgetPath={widgetPath}
              onMouseDown={otherProps.onMouseDown}
              onTouchStart={otherProps.onTouchStart}
            />
          )}
        </Suspense>
      </div>
      {otherProps.widget.displayAlarms && alarms.length > 0 && (
        <div
          style={{
            marginTop: "10px",
            padding: "5px",
            backgroundColor: "#ffe6e6",
            borderRadius: "5px",
          }}
        >
          <h4 style={{ fontSize: "14px", color: "#d32f2f" }}>Active Alarms</h4>
          <ul style={{ listStyleType: "none", padding: "0" }}>
            {alarms.map((alarm, idx) => (
              <li key={idx} style={{ color: "#d32f2f", fontSize: "12px" }}>
                {alarm.title}: Value {alarm.value} ({alarm.direction})
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// Memoize the component to prevent unnecessary re-renders
export default React.memo(Widget, (prevProps, nextProps) => {
  return (
    prevProps.widget === nextProps.widget &&
    prevProps.setPaneArray === nextProps.setPaneArray &&
    _.isEqual(prevProps.widgetPath, nextProps.widgetPath)
  );
});
